import axios, { AxiosError, AxiosResponse, CancelToken } from 'axios';
import { ApiErrorData } from '../types/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { generateNonce } from '../utils/nonce';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.shitlistpoll.com/prod';
const API_KEY = process.env.REACT_APP_API_KEY || '';

// API Configuration
const API_CONFIG = {
  MAX_RETRIES: 3,
  RETRY_DELAY: 1000,
  TIMEOUT: 10000, // 10 seconds
  LONG_TIMEOUT: 30000, // 30 seconds for operations that might take longer
};

// Error types
export enum ApiErrorType {
  NETWORK = 'NETWORK',
  AUTH = 'AUTH',
  API_KEY = 'API_KEY',
  CORS = 'CORS',
  TIMEOUT = 'TIMEOUT',
  SERVER = 'SERVER',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN',
}

// Custom error class
export class ApiError extends Error {
  constructor(
    public type: ApiErrorType,
    public message: string,
    public statusCode?: number,
    public originalError?: any
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Cache the auth token briefly so we don't call fetchAuthSession() on
// every single request.
const TOKEN_CACHE_TTL_MS = 5 * 60 * 1000;
let tokenCache: { token: string; expiresAt: number } | null = null;

// Get authentication token. Returns undefined when there is no active
// session (e.g. anonymous visitors) instead of failing the request —
// public read endpoints only need the API key.
const getAuthToken = async (): Promise<string | undefined> => {
  try {
    if (tokenCache && Date.now() < tokenCache.expiresAt) {
      return tokenCache.token;
    }
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (idToken) {
      tokenCache = { token: idToken, expiresAt: Date.now() + TOKEN_CACHE_TTL_MS };
    }
    return idToken;
  } catch (error) {
    console.warn('No auth session available, continuing without token:', error);
    return undefined;
  }
};

// Create axios instance with default configuration
const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};
if (API_KEY) {
  defaultHeaders['x-api-key'] = API_KEY;
}

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: defaultHeaders,
});

// Retry logic for failed requests
const retryRequest = async (error: AxiosError, retryCount: number = 0): Promise<any> => {
  const shouldRetry = (
    retryCount < API_CONFIG.MAX_RETRIES &&
    (error.code === 'ECONNABORTED' ||
     error.code === 'ETIMEDOUT' ||
     error.response?.status === 429 ||
     (error.response?.status ?? 0) >= 500)
  );

  if (!shouldRetry) {
    throw error;
  }

  const config = error.config;
  if (!config) {
    throw error;
  }

  // Track retries so we don't loop forever on persistent errors
  (config as any)._retryCount = retryCount + 1;

  const delayMs = API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount);
  await new Promise(resolve => setTimeout(resolve, delayMs));
  return api(config);
};

// Request interceptor for authentication and headers
api.interceptors.request.use(async (config) => {
  try {
    // Ensure headers object exists
    config.headers = config.headers || {};

    // Add standard headers
    config.headers['Content-Type'] = 'application/json';
    if (API_KEY) {
      config.headers['x-api-key'] = API_KEY;
    }

    // Add auth token if available
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add timeout if not set
    if (!config.timeout) {
      config.timeout = API_CONFIG.TIMEOUT;
    }

    return config;
  } catch (error) {
    console.error('Error in request interceptor:', error);
    throw error;
  }
}, undefined);

// Response interceptor for error handling and retries
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    // Let cancellations propagate so callers (useApi, etc.) can detect them.
    // Assigned to a boolean so the type-guard narrowing does not collapse
    // `error` to `never` for the rest of the handler.
    const wasCancelled: boolean = axios.isCancel(error);
    if (wasCancelled) {
      throw error;
    }

    // Get retry count from config
    const retryCount = (error.config as any)?._retryCount || 0;

    // Log detailed error information for debugging
    console.debug('API Error:', {
      code: error.code,
      message: error.message,
      status: error.response?.status,
      data: error.response?.data,
      headers: error.response?.headers,
      config: {
        url: error.config?.url,
        method: error.config?.method,
        headers: error.config?.headers,
      }
    });

    // Determine error type and create appropriate error object
    if (!error.response) {
      // Handle network-level errors
      if (error.code === 'ERR_NETWORK') {
        // Check if this is actually a CORS error by examining the error message
        if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
          throw new ApiError(
            ApiErrorType.CORS,
            'Cross-Origin Resource Sharing (CORS) error occurred. This is likely a server configuration issue.',
            0,
            error
          );
        }
        // Otherwise, treat it as a general network error
        throw new ApiError(
          ApiErrorType.NETWORK,
          'Network error occurred. Please check your internet connection.',
          0,
          error
        );
      }
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new ApiError(
          ApiErrorType.TIMEOUT,
          'Request timed out. The server took too long to respond.',
          408,
          error
        );
      }
      throw new ApiError(
        ApiErrorType.NETWORK,
        'Unable to connect to the server. Please check your connection.',
        0,
        error
      );
    }

    // Handle specific HTTP status codes
    switch (error.response.status) {
      case 401:
        throw new ApiError(
          ApiErrorType.AUTH,
          'Your session has expired. Please log in again.',
          401,
          error
        );
      case 403:
        if (error.config?.headers?.['x-api-key']) {
          throw new ApiError(
            ApiErrorType.API_KEY,
            'API key validation failed. Please contact support if this persists.',
            403,
            error
          );
        }
        throw new ApiError(
          ApiErrorType.AUTH,
          'You do not have permission to perform this action.',
          403,
          error
        );
      case 422:
        const errorData = error.response.data as ApiErrorData;
        throw new ApiError(
          ApiErrorType.VALIDATION,
          errorData?.message || 'The submitted data was invalid. Please check your input.',
          422,
          error
        );
      case 429:
        // Retry with exponential backoff for rate limiting
        console.debug('Rate limit hit, retrying with backoff...');
        return retryRequest(error, retryCount);
      default:
        // Handle various server errors
        if (error.response.status >= 500) {
          if (retryCount < API_CONFIG.MAX_RETRIES) {
            console.debug(`Server error (${error.response.status}), attempt ${retryCount + 1} of ${API_CONFIG.MAX_RETRIES}...`);
            return retryRequest(error, retryCount);
          }
          throw new ApiError(
            ApiErrorType.SERVER,
            'The server encountered an error. Please try again later.',
            error.response.status,
            error
          );
        }
        // Handle other HTTP errors
        const responseData = error.response.data as ApiErrorData;
        throw new ApiError(
          ApiErrorType.UNKNOWN,
          responseData?.message || 'An unexpected error occurred.',
          error.response.status,
          error
        );
    }
  }
);

// Officials API
export interface SubmitActData {
  title: string;
  description: string;
  source: string;
  sourceDate: string;
  officialId: string;
  category: string;
  sourceType?: string;
}

export interface Vote {
  actId: string;
  isShitty: boolean;
  timestamp: number;
  officialId: string;
  actDetails?: {
    title: string;
    shittyVotes: number;
    totalVotes: number;
    shittyPercent: number;
  };
}

// Interfaces for API responses
interface PaginatedResponse<T> {
  items: T[];
  lastEvaluatedKey?: string;
  total?: number;
}

interface VoteResponse {
  actId: string;
  isShitty: boolean;
  timestamp: number;
  officialId: string;
}

interface ActResponse {
  id: string;
  title: string;
  description: string;
  source: string;
  sourceDate: string;
  officialId: string;
  category: string;
  shittyVotes: number;
  totalVotes: number;
  shittyPercent: number;
  moderationStatus: string;
  timestamp: number;
}

interface OfficialResponse {
  id: string;
  name: string;
  title: string;
  position: string;
  state: string;
  jurisdiction: string;
  party: string;
  shittyVotes: number;
  totalVotes: number;
  actCount: number;
  lastUpdated: number;
  createdAt: number;
}

// API Request Options interface
interface RequestOptions {
  cancelToken?: CancelToken;
  timeout?: number;
}

// Votes API with enhanced error handling and request cancellation
export const votesApi = {
  submit: async (
    data: { actId: string; isShitty: boolean; officialId: string },
    options: RequestOptions = {}
  ): Promise<VoteResponse> => {
    try {
      // Generate nonce for write operation
      const nonce = await generateNonce();
      
      const response = await api.post('/votes', {
        ...data,
        nonce
      }, {
        ...options,
        timeout: API_CONFIG.LONG_TIMEOUT, // Use longer timeout for submissions
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error submitting vote:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to submit vote',
        500,
        error
      );
    }
  },

  getVote: async (
    actId: string,
    options: RequestOptions = {}
  ): Promise<VoteResponse | null> => {
    try {
      const response = await api.get(`/votes/${actId}`, options);
      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }
      // 404 means no vote exists, which is a valid state
      if ((error as AxiosError<ApiErrorData>).response?.status === 404) {
        return null;
      }
      console.error('Error getting vote:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to get vote details',
        500,
        error
      );
    }
  },

  list: async (
    params?: { limit?: number; lastKey?: string },
    options: RequestOptions = {}
  ): Promise<PaginatedResponse<VoteResponse>> => {
    try {
      const response = await api.get('/votes', { 
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error listing votes:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to list votes',
        500,
        error
      );
    }
  },

  delete: async (
    actId: string,
    options: RequestOptions = {}
  ): Promise<void> => {
    try {
      await api.delete(`/votes/${actId}`, options);
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error deleting vote:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to delete vote',
        500,
        error
      );
    }
  },
};

// Acts API with enhanced error handling and request cancellation
export const actsApi = {
  list: async (
    params?: {
      officialId?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      limit?: number;
      page?: number;
    },
    options: RequestOptions = {}
  ): Promise<PaginatedResponse<ActResponse>> => {
    try {
      const response = await api.get('/acts', {
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error listing acts:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to list acts',
        500,
        error
      );
    }
  },

  getById: async (
    id: string,
    options: RequestOptions = {}
  ): Promise<ActResponse> => {
    try {
      const response = await api.get(`/acts/${id}`, options);
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error getting act details:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to get act details',
        500,
        error
      );
    }
  },

  submit: async (
    data: SubmitActData,
    options: RequestOptions = {}
  ): Promise<ActResponse> => {
    try {
      // Generate nonce for write operation
      const nonce = await generateNonce();

      const response = await api.post('/acts', {
        ...data,
        nonce
      }, {
        ...options,
        timeout: API_CONFIG.LONG_TIMEOUT, // Use longer timeout for submissions
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error submitting act:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to submit act',
        500,
        error
      );
    }
  },

  moderate: async (
    id: string,
    data: { status: string; notes?: string },
    options: RequestOptions = {}
  ): Promise<ActResponse> => {
    try {
      // Generate nonce for write operation
      const nonce = await generateNonce();

      const response = await api.post(`/acts/${id}/moderate`, {
        ...data,
        nonce
      }, options);
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error moderating act:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to moderate act',
        500,
        error
      );
    }
  },
};

// Officials API with enhanced error handling and request cancellation
export const officialsApi = {
  list: async (
    params?: {
      state?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      limit?: number;
      page?: number;
    },
    options: RequestOptions = {}
  ): Promise<PaginatedResponse<OfficialResponse>> => {
    try {
      const response = await api.get('/officials', {
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error listing officials:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to list officials',
        500,
        error
      );
    }
  },

  getById: async (
    id: string,
    options: RequestOptions = {}
  ): Promise<OfficialResponse> => {
    try {
      const response = await api.get(`/officials/${id}`, options);
      return response.data.official;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error getting official details:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to get official details',
        500,
        error
      );
    }
  },

  getActs: async (
    officialId: string,
    params?: {
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      limit?: number;
      page?: number;
    },
    options: RequestOptions = {}
  ): Promise<PaginatedResponse<ActResponse>> => {
    try {
      const response = await api.get(`/officials/${officialId}/acts`, {
        params,
        ...options,
      });
      return response.data;
    } catch (error) {
      // Let request cancellations propagate to callers (hooks detect isCancel)
      if (axios.isCancel(error)) {
        throw error;
      }
      console.error('Error getting official acts:', error);
      throw error instanceof ApiError ? error : new ApiError(
        ApiErrorType.UNKNOWN,
        'Failed to get official acts',
        500,
        error
      );
    }
  },
};

// Function to create a cancellation token
export const createCancelToken = () => axios.CancelToken.source();

export default api;